import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './upload-configs-panel.html';
import type { UploadConfig } from '../services/api.types';
import { deleteUploadConfig, getUserSettings, listUploadConfigs, upsertUserSetting } from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';

@customElement({ name: 'upload-configs-panel', template })
export class UploadConfigsPanel {
  private readonly i18n: I18N = resolve(I18N);

  public configs: UploadConfig[] = [];
  public errorMessage = '';
  public showModal = false;
  public configToEdit: UploadConfig | null = null;
  public debugDescription = false;

  public async attached(): Promise<void> {
    await Promise.all([this.fetchConfigs(), this.loadSettings()]);
  }

  private async loadSettings(): Promise<void> {
    try {
      const settings = await getUserSettings();
      const s = settings.find(x => x.key === 'uploadDebugDescription');
      this.debugDescription = s?.value === 'true';
    } catch { /* ignore */ }
  }

  public async toggleDebugDescription(): Promise<void> {
    try {
      await upsertUserSetting('uploadDebugDescription', this.debugDescription.toString());
    } catch { /* ignore */ }
  }

  public async fetchConfigs(): Promise<void> {
    try {
      this.configs = await listUploadConfigs();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public openCreateModal(): void {
    this.configToEdit = null;
    this.showModal = true;
  }

  public openEditModal(config: UploadConfig): void {
    this.configToEdit = config;
    this.showModal = true;
  }

  public onModalSaved(config: UploadConfig): void {
    const idx = this.configs.findIndex(c => c.id === config.id);
    if (idx >= 0) {
      this.configs[idx] = config;
      this.configs = [...this.configs];
    } else {
      this.configs = [...this.configs, config].sort((a, b) => a.name.localeCompare(b.name));
    }
    this.showModal = false;
    this.configToEdit = null;
  }

  public onModalClosed(): void {
    this.showModal = false;
    this.configToEdit = null;
  }

  public formatSeconds(valueMs: number): string {
    return this.i18n.tr('uploadConfigs.seconds', { count: valueMs / 1000 });
  }

  public async removeConfig(config: UploadConfig): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('uploadConfigs.delete.confirm', { name: config.name }),
      this.i18n.tr('uploadConfigs.delete.confirmTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteUploadConfig(config.id);
      this.configs = this.configs.filter(c => c.id !== config.id);
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }
}
